/**
 * Telephony API
 * API для работы с телефонией CRM (звонки, записи, WebRTC, PBX)
 */
import { apiClient } from '@/api'
import type {
  Call,
  CallsFilter,
  PaginationParams,
  PaginatedCalls,
  InitiateCallRequest,
  InitiateCallResponse,
  CallRecordingResponse,
  CallTranscription,
  WebRTCKeyResponse,
  ManagerPBXStatus,
  ServicePhone,
  SetupManagerRequest,
  SetupManagerResponse,
  SipCredentials,
} from '@/features/telephony/types/telephony.types'

const BASE_PATH = '/api/v1/telephony'

function mapCallFromAPI(raw: Record<string, unknown>): Call {
  return {
    id: (raw.id as string) || '',
    pbxCallId: (raw.pbxCallId as string) || '',
    callId: (raw.callId as string) || '',
    managerId: (raw.managerId as string) || '',
    clientId: raw.clientId as string | undefined,
    direction: ((raw.direction as string) || 'in') as Call['direction'],
    callerNumber: (raw.callerNumber as string) || '',
    calleeNumber: (raw.calleeNumber as string) || '',
    internalNumber: (raw.internalNumber as string) || '',
    disposition: ((raw.disposition as string) || 'ringing') as Call['disposition'],
    duration: (raw.duration as number) || 0,
    isRecorded: (raw.isRecorded as boolean) || false,
    recordingFileId: raw.recordingFileId as string | undefined,
    recordingUrl: raw.recordingUrl as string | undefined,
    startedAt: raw.startedAt as string | undefined,
    answeredAt: raw.answeredAt as string | undefined,
    endedAt: raw.endedAt as string | undefined,
    createdAt: (raw.createdAt as string) || '',
    updatedAt: (raw.updatedAt as string) || '',
  }
}

export const telephonyApi = {
  /**
   * Получить список звонков с фильтрами и пагинацией
   * GET /api/v1/telephony/calls
   */
  async listCalls(filter?: CallsFilter, pagination?: PaginationParams): Promise<PaginatedCalls> {
    // DEV-302: пагинация — вложенный объект vinprop.common.v1.PageRequest (uint64 → строки)
    const params: Record<string, string | number | undefined> = {
      'pagination.page': pagination?.page,
      'pagination.perPage': pagination?.pageSize,
      managerId: filter?.managerId,
      clientId: filter?.clientId,
      direction: filter?.direction,
      dateFrom: filter?.dateFrom,
      dateTo: filter?.dateTo,
    }

    // Удаляем undefined
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) delete params[key]
    })

    const response = await apiClient.get(`${BASE_PATH}/calls`, { params })

    const calls = (response.data.calls || []).map(mapCallFromAPI)
    // total — uint64 внутри pagination, protojson отдаёт строкой
    const total = parseInt(response.data.pagination?.total ?? '0', 10) || 0
    const page = pagination?.page || 1
    const pageSize = pagination?.pageSize || 20

    return {
      calls,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
    }
  },

  /**
   * Инициировать звонок (callback)
   * POST /api/v1/telephony/calls
   */
  async initiateCall(data: InitiateCallRequest): Promise<InitiateCallResponse> {
    const response = await apiClient.post(`${BASE_PATH}/calls`, {
      phoneNumber: data.phoneNumber,
      clientId: data.clientId,
      sipMappingId: data.sipMappingId,
      callerId: data.callerId,
    })

    return {
      callId: response.data.callId || '',
      pbxCallId: response.data.pbxCallId || '',
      status: response.data.status || '',
    }
  },

  /**
   * Получить presigned URL записи звонка
   * GET /api/v1/telephony/calls/{callId}/recording
   */
  async getCallRecording(callId: string): Promise<CallRecordingResponse> {
    const response = await apiClient.get(`${BASE_PATH}/calls/${callId}/recording`)

    return {
      url: response.data.url || '',
      expiresAt: response.data.expiresAt || '',
    }
  },

  /**
   * Получить WebRTC ключ для браузерных звонков
   * GET /api/v1/telephony/webrtc/key
   */
  async getWebRTCKey(domain?: string): Promise<WebRTCKeyResponse> {
    const params: Record<string, string> = {}
    if (domain) {
      params.domain = domain
    }
    const response = await apiClient.get(`${BASE_PATH}/webrtc/key`, { params })

    return {
      key: response.data.key || '',
      sipId: response.data.sipId || '',
      expiresAt: response.data.expiresAt || '',
      sipDomain: response.data.sipDomain || '',
      sipUsername: response.data.sipUsername || '',
      sipPassword: response.data.sipPassword || '',
    }
  },

  /**
   * Зарегистрировать контекст WebRTC звонка перед инициацией
   * POST /api/v1/telephony/webrtc/register-call
   */
  async registerWebRTCCall(clientId: string, phoneNumber: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`${BASE_PATH}/webrtc/register-call`, {
      clientId,
      phoneNumber,
    })
    return {
      success: response.data.success || false,
    }
  },

  /**
   * Получить PBX статус менеджера (online/offline)
   * GET /api/v1/telephony/managers/{managerId}/status
   */
  async getManagerPBXStatus(managerId: string): Promise<ManagerPBXStatus> {
    const response = await apiClient.get(`${BASE_PATH}/managers/${managerId}/status`)

    return {
      isOnline: response.data.isOnline || false,
      sipId: response.data.sipId || '',
    }
  },

  /**
   * Получить транскрипцию и AI-анализ звонка
   * GET /api/v1/telephony/calls/{callId}/transcription
   */
  async getCallTranscription(callId: string): Promise<CallTranscription | null> {
    try {
      const response = await apiClient.get(`${BASE_PATH}/calls/${callId}/transcription`)
      const t = response.data.transcription
      if (!t) return null

      let phrases = []
      if (t.phrasesJson) {
        try {
          phrases = JSON.parse(t.phrasesJson)
        } catch {
          phrases = []
        }
      }

      let aiAnalysis = null
      if (t.aiClientQualificationJson) {
        try {
          aiAnalysis = JSON.parse(t.aiClientQualificationJson)
        } catch {
          aiAnalysis = null
        }
      }

      return {
        id: t.id || '',
        callId: t.callId || '',
        language: t.language || '',
        recognitionStatus: t.recognitionStatus || 'in_progress',
        rawText: t.rawText || '',
        phrases,
        aiSummary: t.aiSummary || '',
        aiAnalysis,
        processedAt: t.processedAt || undefined,
        createdAt: t.createdAt || '',
        updatedAt: t.updatedAt || '',
      }
    } catch {
      return null
    }
  },

  /**
   * Получить список доступных телефонных номеров для исходящих звонков
   * GET /api/v1/telephony/phones
   */
  async listServicePhones(): Promise<ServicePhone[]> {
    const response = await apiClient.get(`${BASE_PATH}/phones`)

    return (response.data.phones || []).map((p: Record<string, unknown>) => ({
      id: (p.id as string) || '',
      callerId: (p.callerId as string) || '',
      internalNumber: (p.internalNumber as string) || '',
      sipId: (p.sipId as string) || '',
      isActive: (p.isActive as boolean) || false,
      country: (p.country as string) || '',
      description: (p.description as string) || '',
    }))
  },

  /**
   * Настроить Zadarma SIP для менеджера
   * POST /api/v1/telephony/managers/setup
   */
  async setupManagerZadarma(data: SetupManagerRequest): Promise<SetupManagerResponse> {
    const response = await apiClient.post(`${BASE_PATH}/managers/setup`, {
      managerId: data.managerId,
      servicePhoneId: data.servicePhoneId,
    })

    return {
      sipId: response.data.sipId || '',
      internalNumber: response.data.internalNumber || '',
      callerId: response.data.callerId || '',
    }
  },

  /**
   * Получить SIP-credentials для JsSIP клиента
   * GET /api/v1/telephony/sip/credentials
   */
  async getSipCredentials(): Promise<SipCredentials> {
    const response = await apiClient.get(`${BASE_PATH}/sip/credentials`)

    return {
      wssUrl: response.data.wssUrl || '',
      sipUri: response.data.sipUri || '',
      sipLogin: response.data.sipLogin || '',
      sipPassword: response.data.sipPassword || '',
      sipDomain: response.data.sipDomain || '',
      displayName: response.data.displayName || '',
    }
  },
}
